import { firestore } from 'firebase';
import DatabaseAdapter from 'src/firebase/db/DatabaseAdapter';
import UsersDB from 'src/firebase/db/Users';
import OrdersDB, { OrderEntity } from 'src/firebase/db/Orders';

import { ReferenceError, InternalError } from 'src/firebase/db/ApplicationError';

export type OrderBreadProps = OrderBreadEntity & {
  original_orders: string[];
};

export const orderBreadInitialEntity = {
  id: '',
  selected: false,
  bread_id: '',
  shop_id: '',
  shop_name: '',
  shop_address: '',
  image: '',
  name: '',
  description: '',
  required_baking_minutes: 0,
  can_order_time: undefined as Date | undefined,
  last_pickup_time: undefined as Date | undefined,
  price: 0,
  stock: 0,
  maximum_order: 0,
  pickup_supplement: '',
  width: '',
  depth: '',
  height: '',
  best_before: '',
  is_reservation: false,
  is_notified: false,
  notifications_ref: {} as firebase.firestore.DocumentReference,
  created_at: {} as firestore.Timestamp,
  updated_at: {} as firestore.Timestamp,
  stats: {
    userCount: 0,
  },
  orders: [] as OrderEntity[],
};
export type OrderBreadEntity = typeof orderBreadInitialEntity;
export const orderBreadEntityKeys = (Object.keys(
  orderBreadInitialEntity,
) as unknown) as (keyof OrderBreadEntity)[];

class OrderBreadsDB extends DatabaseAdapter {
  constructor(props: any) {
    super(props);
    this.setSchemaKeys(orderBreadEntityKeys);
  }

  getOrderBread(id: string) {
    return this.getDocumentRef(id)
      .get()
      .then(async (doc) => {
        if (!doc.exists) {
          console.log('No matching documents.');
          throw new ReferenceError('焼き上がりをお知らせできるパンが存在しません');
        }
        const orderBread = (await doc.data()) as OrderBreadEntity;
        return this.formatSuccess<OrderBreadEntity>(orderBread);
      })
      .catch((err) => {
        console.error(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  getOrderBreads(shopId: string) {
    const orderBreads: OrderBreadEntity[] = [];
    return this.getConnection()
      .where('shop_id', '==', shopId)
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          console.log('No matching documents.');
          throw new ReferenceError('焼き上がりをお知らせできるパンが存在しません');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const stats = await this.formatStats(data);
            orderBreads.push({
              ...data,
              stats: stats,
            } as OrderBreadEntity);
          }),
        );
        return this.formatSuccess<OrderBreadEntity[]>(orderBreads);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 通知に紐づく明細を取得
  async getNotificationOrderBreads(
    shopId: string,
    notificationRef: firebase.firestore.DocumentReference,
    isAdmin?: boolean,
  ) {
    const orderBreads: OrderBreadProps[] = [];
    let query = this.getConnection()
      .where('notifications_ref', '==', notificationRef)
      .orderBy('created_at', 'asc');
    if (!isAdmin) {
      query = query.where('shop_id', '==', shopId);
    }
    return await query
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError('通知に紐づくパンが存在しません');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = await doc.data();
            const shop_id = data.shop_id as string;
            const stats = await this.formatStats(data);

            // 紐づく注文データをセット
            let orders = [] as OrderEntity[];
            try {
              const ordersDoc = await OrdersDB.getNotificationOrders(shop_id, notificationRef);
              orders = ordersDoc.data;
            } catch (e) {
              console.error(e);
              // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
              orders = [];
            }
            orderBreads.push({
              ...data,
              stats,
              orders: orders as OrderEntity[],
              original_orders: data.orders,
            } as OrderBreadProps);
          }),
        );
        return this.formatSuccess<OrderBreadProps[]>(orderBreads);
      })
      .catch((err) => {
        console.log(`shopId - ${shopId} ... notification: ${notificationRef.id}`);
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 期間で通知明細を取得する
  getDayOrderBreads(shopId: string, startDate: Date, endDate: Date) {
    const orderBreads: OrderBreadEntity[] = [];
    return this.getConnection()
      .where('shop_id', '==', shopId)
      .orderBy('created_at', 'asc')
      .startAt(startDate)
      .endAt(endDate)
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError('焼き上がりをお知らせできるパンが存在しません');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            const stats = await this.formatStats(data);
            // 紐づく注文データをセット
            let orders = [] as OrderEntity[];
            try {
              const ordersDoc = await OrdersDB.getOrders(data.id);
              orders = ordersDoc.data;
            } catch (e) {
              // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
              orders = [];
            }
            orderBreads.push({
              ...data,
              stats,
              orders,
            } as OrderBreadEntity);
          }),
        );
        return this.formatSuccess<OrderBreadEntity[]>(orderBreads);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 新規作成
  async create(query: Partial<OrderBreadEntity>) {
    const filteredQuery = this.filterQuery(query);
    const requestQuery = {
      ...filteredQuery,
      id: '',
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
      is_notified: false,
    };

    const ref = this.getConnection();
    return ref
      .add(requestQuery)
      .then(async (docRef) => {
        const id = docRef.id;
        const data = await this.getDocument(id);
        const { created_at, can_order_time, last_pickup_time } = data;
        const serverTimeStamp = created_at.toDate() as Date;

        /*
         * ブラウザ開きっぱなし？だったりでクライアントサイドの過去日付を参照してしまい、配信後に即受付終了...みたいな事態が発生。
         * 更新後にFirebase側のタイムスタンプを参照して、時刻以外をサーバ準拠の本日日付に確実に強制更新する処理を追加。
         * 他にいい方法があれば...
         */
        const updateDateByServerTimeStamp = (target: Date) => {
          const now = new Date(serverTimeStamp);
          const date = new Date(target);
          date.setFullYear(now.getFullYear());
          date.setMonth(now.getMonth());
          date.setDate(now.getDate());
          return date;
        };
        const canOrderTime = updateDateByServerTimeStamp(can_order_time.toDate() as Date);
        const lastPickupTime = updateDateByServerTimeStamp(last_pickup_time.toDate() as Date);

        await ref.doc(docRef.id).update({
          id,
          can_order_time: canOrderTime,
          last_pickup_time: lastPickupTime,
        });
        return this.formatSuccess({
          id: id,
        });
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  // 更新
  async update(query: Partial<OrderBreadEntity>) {
    const { id } = query;
    const filteredQuery = this.filterQuery(query);
    const requestQuery = {
      ...filteredQuery,
    };
    // 更新対象外
    delete requestQuery['orders'];
    delete requestQuery['stats'];

    if (!id) {
      throw new InternalError("order_breads's id is empty.");
    }

    const ref = this.getDocumentRef(id);
    return ref
      .update(requestQuery)
      .then(async () => {
        return this.formatSuccess(requestQuery);
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  // 通知に紐づくデータとして新規作成する
  async createByNotification(query: {
    notifications_ref: firestore.DocumentReference;
    bread_id: string;
    shop_id: string;
    shop_name: string;
    shop_address: string;
    description: string;
    image: string;
    name: string;
    price: number;
    stock: number;
    maximum_order: number;
    last_pickup_time: Date | undefined;
    can_order_time: Date | undefined;
    stats: { userCount: number };
  }) {
    const { last_pickup_time, can_order_time } = query;
    const requestQuery = {
      ...query,
      id: '',
      required_baking_minutes: 0,
      pickup_supplement: '',
      is_notified: false,
      orders: [],
      last_pickup_time: last_pickup_time || null,
      can_order_time: can_order_time || null,
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
    };

    const ref = this.getConnection();
    return ref
      .add(requestQuery)
      .then(async (docRef) => {
        const id = docRef.id;

        await ref.doc(id).update({ id });
        return this.formatSuccess({
          id,
          ref: docRef,
        });
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  // 削除
  async delete(id: string) {
    const docRef = this.getDocumentRef(id);
    const orderBread = (await (await docRef.get()).data()) as OrderBreadEntity;
    return docRef
      .delete()
      .then(async () => {
        return this.formatSuccess(orderBread);
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }

  formatStats = async (data: firebase.firestore.DocumentData) => {
    const userCount = await UsersDB.getRegisterdBakedBreadsUserCount(data.id);
    return {
      userCount: userCount,
    };
  };
}

export default new OrderBreadsDB({
  schema: 'order_breads',
});
